<template>
  <div class="InsurancePersonalDataStep4Form">
    <h2 class="gl_form-title small">
      {{ title }}
    </h2>

    <CardInfoComponent
      v-if="message"
      :icon="message.icon"
    >
      {{ message.label }}
    </CardInfoComponent>

    <template v-if="showRadioEmail">
      <p class="label">{{ t('hiring.common.insurance-personal-data-step-5.use-this-email') }}</p>

      <RadioButtonGroup
        id="email-options"
        :value="emailSelection"
        :options="emailOptions"
        v-model="emailSelection"
        flexDirection="column"
      />
    </template>

    <template v-if="!showRadioEmail || !stringToBoolean(emailSelection)">
      <EmailComponent
        id="email"
        icon="icon-email1"
        :label="emailLabel"
        :placeholder="emailPlaceholder"
        v-model="email"
        :rules="[validationRules.required]"
      />

      <BaseInput
        :key="'email-confirm'"
        id="email-confirm"
        icon="icon-email1"
        :label="t('hiring.common.insurance-personal-data-step-4.confirm-email')"
        :placeholder="emailConfirmPlaceholder"
        :inputmode="InputmodeTypes.Email"
        :inputType="InputTypes.Email"
        v-model="emailConfirm"
        @paste="(e) => e.preventDefault()"
        autocomplete="nope"
        :rules="[
          validationRules.required,
          validationRules.validEmail,
          validationRules.confirmEmail
        ]"
      />
    </template>

    <h3 class="gl_heading flexi_column">
      <span>{{
        t('hiring.common.insurance-personal-data-step-4.also-provide-a-contact-phone')
      }}</span>
      <span v-if="!prefixEnabled">{{
        t('hiring.common.insurance-personal-data-step-4.also-provide-a-contact-phone-2')
      }}</span>
    </h3>

    <BaseSelect
      id="prefixes"
      :label="t('hiring.common.insurance-personal-data-step-4.prefix')"
      v-model="prefix"
      :disabled="!prefixEnabled"
      :items="prefixes"
    />

    <BaseInput
      id="phone"
      :label="t('hiring.common.insurance-personal-data-step-4.contact-phone')"
      :placeholder="t('hiring.common.insurance-personal-data-step-4.write-a-contact-phone')"
      :inputmode="InputmodeTypes.Telephone"
      :inputType="InputTypes.Telephone"
      v-model="phone"
      :maxlength="maxLength"
      :rules="[validationRules.required, validationRules.validMobile]"
    />

    <template v-if="showConsents">
      <h3 class="gl_heading secondary">
        {{ t('hiring.common.insurance-personal-data-step-4.data-protection') }}
      </h3>

      <FoldingText
        class="mb-3"
        :text="t('copy_policies_description_info_send')"
      />

      <YesNoComponent
        id="advertising"
        :legend="t('copy_consent-written-advertising')"
        :value="thirdPartyAdvertising"
        v-model="thirdPartyAdvertising"
        :rules="[validationRules.yesNoRequired]"
      />

      <YesNoComponent
        id="call"
        :legend="t('copy_consent-phone-commercial-info')"
        :value="dataTransfer"
        v-model="dataTransfer"
        :rules="[validationRules.yesNoRequired]"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
  import { onBeforeMount, ref, watch, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { stringToBoolean } from '@/utils/converterUtils'
  import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
  import EmailComponent from '@/modules/core/components/formFields/EmailComponent.vue'
  import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import YesNoComponent from '@/modules/core/components/formFields/YesNoComponent.vue'
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import FoldingText from '@/modules/core/components/utils/FoldingText.vue'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import type { OptionsValuesInterface } from '@/modules/core/models/types'
  import { UserRoleEnum } from '@/modules/core/models/enums'
  import Insured from '@/modules/common/common/models/APP/Insured'
  import InputmodeTypes from '@/modules/core/models/InputmodeTypes'
  import InputTypes from '@/modules/core/models/InputTypes'
  import { SPAIN_PREFIX } from '@/modules/core/constants/common'
  import BranchEnum from '@/modules/core/models/BranchEnum'

  enum EmailSelectionOption {
    HOLDER = '1',
    OTHER = '0'
  }
  type EmailSelectionValue = '1' | '0'
  type ConsentState = boolean | null

  const { t } = useI18n()
  const commonStore = useCommonStore()
  const loadingStore = useLoadingStore()
  const { required, yesNoRequired, validMobile, validEmail } = useRules()

  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
    extra?: Record<string, any>
  }
  const props: PropsInterface = withDefaults(defineProps<PropsInterface>(), {})

  const validationRules = {
    required,
    yesNoRequired,
    validEmail,
    validMobile: (value: string) => validMobile(value, prefix.value ?? ''),
    confirmEmail: (value: string) =>
      value.toUpperCase() === email.value?.toUpperCase() ||
      t('hiring.common.insurance-personal-data-step-4.emails-do-not-match')
  }

  const prefixEnabled = computed(() => props.extra?.prefixEnable ?? false)

  const currentInsured = new Insured(
    props.extra?.customInsuredProp
      ? props.answers[props.extra.customInsuredProp]
      : props.answers.currentInsured
  )
  const holder = new Insured(
    props.extra?.usePolicyHolderAsHolder ? props.answers.policyHolder : props.answers.holder
  )

  const emailOptions: OptionsValuesInterface[] = [
    {
      label: holder.userDetails.email!,
      value: '1'
    },
    {
      label: t('hiring.common.insurance-personal-data-step-5.another-email'),
      value: '0'
    }
  ]
  const insuredName = currentInsured.userDetails.name
  const currentInsuredUser = currentInsured.userDetails

  const defaultEmailSelection = computed((): EmailSelectionOption => {
    const holderEmail = holder.userDetails.email
    const insuredEmail = currentInsured.userDetails.email

    if (!holderEmail) {
      return EmailSelectionOption.OTHER
    }

    if (!insuredEmail || holderEmail === insuredEmail) {
      return EmailSelectionOption.HOLDER
    }

    return EmailSelectionOption.OTHER
  })
  const emailSelection = ref<EmailSelectionValue>(defaultEmailSelection.value)
  watch(emailSelection, (option) => {
    if (option === EmailSelectionOption.HOLDER) {
      email.value = currentInsuredUser.email ?? ''
      emailConfirm.value = currentInsuredUser.email ?? ''
    } else {
      email.value = ''
      emailConfirm.value = ''
    }
  })

  const email = ref<string>(currentInsuredUser.email ?? '')
  const emailConfirm = ref<string>(currentInsuredUser.email ?? '')
  const phone = ref<string | null>(currentInsuredUser.phone)
  const prefixes = ref<OptionsValuesInterface[]>([])
  const prefix = ref<string | null>(currentInsuredUser.prefix)
  const telephoneAdvertising = currentInsured.thirdPartyTelephoneAdvertising
  const writtenAdvertising = currentInsured.thirdPartyWrittenAdvertising
  const thirdPartyAdvertisingIsNull = telephoneAdvertising === null || writtenAdvertising === null
  const thirdPartyAdvertising = ref<ConsentState>(
    !thirdPartyAdvertisingIsNull ? telephoneAdvertising && writtenAdvertising : null
  )
  const dataTransfer = ref<ConsentState>(holder.dataTransfer)
  const showConsents = computed(() => isHolder.value && !holder.thirdPartiesAnswered)
  const isHolder = computed(() => currentInsured.isHolder)
  const maxLength = computed(() => (prefix.value === SPAIN_PREFIX ? 9 : 15))
  const message = computed(() => {
    if (props.extra?.customAlertMessage) {
      return props.extra.customAlertMessage
    }
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    if (isInsured) {
      return props.configurations.tags?.contactDataInsuredAlert ?? ''
    } else {
      return props.configurations.tags?.contactDataAlert ?? ''
    }
  })
  const showRadioEmail = computed(() => {
    const holderEmail = holder.userDetails.email
    const validRoles = [UserRoleEnum.HOLDER_NOT_INSURED, UserRoleEnum.HOLDER_INSURED]
    return validRoles.includes(currentInsured.role) && !(!holderEmail || holderEmail.length === 0)
  })

  const title = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'

    return t(`hiring.common.insurance-personal-data-step-4.requesting-contact-details.${i18n}`, {
      name: insuredName
    })
  })

  const emailLabel = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'

    return t(`hiring.common.insurance-personal-data-step-4.email-for-contract-data.${i18n}`)
  })

  const emailPlaceholder = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'

    return t(`hiring.common.insurance-personal-data-step-4.write-your-email.${i18n}`)
  })

  const emailConfirmPlaceholder = computed(() => {
    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const i18n = isInsured ? 'not-holder' : 'holder'

    return t(`hiring.common.insurance-personal-data-step-4.confirm-your-email.${i18n}`)
  })

  function triggerSubmit() {
    const page = props.nextForm[0]
    const email =
      !showRadioEmail.value || !stringToBoolean(emailSelection.value)
        ? emailConfirm.value
        : holder.userDetails.email

    currentInsured.userDetails.email = email
    currentInsured.userDetails.phone = phone.value
    currentInsured.userDetails.prefix = prefix.value

    if (showConsents.value) {
      currentInsured.thirdPartyTelephoneAdvertising = thirdPartyAdvertising.value // MID saves together
      currentInsured.thirdPartyWrittenAdvertising = thirdPartyAdvertising.value // MID saves together
      currentInsured.dataTransfer = dataTransfer.value
    }

    let answers
    if (props.extra?.customInsuredProp) {
      answers = { [props.extra.customInsuredProp]: currentInsured }
    } else if (props.extra?.currentInsuredIndex === 'holder') {
      answers = { holder: currentInsured }
    } else {
      answers = {
        userDetails: currentInsured.userDetails,
        thirdPartyTelephoneAdvertising: currentInsured.thirdPartyTelephoneAdvertising,
        thirdPartyWrittenAdvertising: currentInsured.thirdPartyWrittenAdvertising,
        dataTransfer: currentInsured.dataTransfer
      }
    }

    return { page, answers }
  }

  async function beforeMount() {
    loadingStore.startLoading()
    try {
      prefixes.value = await commonStore.fetchCountryPrefixes()

      if (
        props.configurations.businessLine === BranchEnum.FOREIGN_HEALTH &&
        currentInsured.addressDetails.country !== 'ESPA' &&
        !prefix.value
      ) {
        const country = (currentInsured.addressDetails.country ?? '').trim()
        prefix.value =
          prefixes.value.find((p) => {
            const codes = p.extra?.code as string[] | undefined
            return !!codes?.includes(country)
          })?.value ?? SPAIN_PREFIX
      }

      if (!prefix.value) {
        prefix.value = SPAIN_PREFIX
      }
    } finally {
      loadingStore.stopLoading()
    }
  }

  onBeforeMount(beforeMount)
</script>

<style lang="scss" scoped>
  .InsurancePersonalDataStep4Form {
    .label {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
      margin-bottom: $spacing-s;
    }

    :deep(label.v-label.v-label--clickable) {
      opacity: 1;
      font-weight: $font-weight-700;
    }

    .phone-contact {
      margin-bottom: 5%;
      color: $color-neutral;
      font-size: $font-size-20;
    }

    .flexi_column {
      display: flex;
      flex-direction: column;
    }

    :deep(i.icon-sobre) {
      font-size: $font-size-2xl !important;
    }
  }
</style>
